import jira from '../../assets/jira.svg';
import youtrack from '../../assets/youtrack.svg';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️ THE TRACKER'S OWN MARK — items 18 and 48.                              │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The product owner supplied `Jira.svg` and `YouTrack_Icon.svg` on 9 Sep 2026 (D9); this is where
 * they enter the app.
 *
 * ⚠️⚠️ **NOT AN `Icon` NAME, AND THE ICON SET IS WHY.** `icon.tsx` is `lucide-react` reached by
 * name — one stroke weight, one colour, `currentColor` throughout. A vendor logo is none of those:
 * Jira's is a two-tone gradient and YouTrack's is four colours, and forcing either through the
 * icon set would either flatten it to a monochrome silhouette nobody recognises or put the first
 * multi-colour path into a component whose whole contract is that it has none.
 *
 * ⚠️ **`<img>` with a bundled URL, not inlined markup.** Vite hashes and caches the file, two
 * copies of the same logo on one screen download once, and a vendor's artwork stays a file we can
 * replace when they redraw it rather than a paragraph of paths in a `.tsx`.
 *
 * ⚠️⚠️ **AN UNKNOWN PROVIDER DRAWS NOTHING, NOT A PLACEHOLDER.** A generic square beside somebody's
 * Jira account says *"we could not identify this"* about a row that is perfectly well identified;
 * the instance name beside it already says which tracker it is.
 *
 * ⚠️ **Decorative by default.** The logo sits beside the provider's name in every current caller,
 * and an `alt` would make a screen reader read *"Jira"* twice. A caller that draws it ALONE passes
 * a `label`, which is the only case where it carries meaning.
 */
const LOGOS: Readonly<Record<string, string>> = { jira, youtrack };

export function ProviderLogo({
  provider,
  size = 14,
  label,
  className,
}: {
  readonly provider: string | null | undefined;
  readonly size?: number;
  /** Already translated. Omit where the provider's NAME is beside it — the usual case. */
  readonly label?: string;
  readonly className?: string;
}) {
  const src = provider === null || provider === undefined ? undefined : LOGOS[provider];
  if (src === undefined) return null;

  return (
    <img
      src={src}
      /* ⚠️ `''` and `aria-hidden` together: an empty alt alone still leaves the image in the tree
         for some readers, and a missing alt makes it announce its filename. */
      alt={label ?? ''}
      {...(label === undefined ? { 'aria-hidden': true } : {})}
      width={size}
      height={size}
      className={className}
      style={{ width: size, height: size }}
    />
  );
}

/**
 * Whether we hold artwork for a provider - item 18.
 *
 * ⚠️ **Exported so a caller can draw its own fallback**, which `ProviderIcon` needs: it puts the
 * mark inside a coloured tile, and a tile with nothing in it is worse than the glyph it replaced.
 * `ProviderLogo` itself returns `null` for an unknown provider, which is right where the mark
 * stands alone and not enough where something has to take its place.
 */
export function hasLogo(provider: string | null | undefined): boolean {
  return provider !== null && provider !== undefined && LOGOS[provider] !== undefined;
}
